import { formatDate } from '@angular/common';
import { DatePipe } from '@angular/common';
import { inject, LOCALE_ID, Pipe, PipeTransform, Provider } from '@angular/core';
import { appTimeZoneOffset, formatCalendarDate, isDateOnlyString, toAppInstant } from './time-zone.config';

/**
 * Drop-in replacement for Angular's DatePipe that renders every value in the
 * app's configured time zone (see APP_TIME_ZONE) and treats tz-less backend
 * timestamps as GMT.
 *
 * It accepts the exact same format tokens as DatePipe, so `| lsDate: 'MM/dd/yyyy h:mm a'`
 * behaves like `| date: '...'` — only the time zone differs. Any `timezone`
 * argument passed by callers is intentionally ignored: the whole point is that
 * the zone is centralized, not per-call.
 *
 * Bare calendar dates ("2026-07-01") are the exception: they carry no instant,
 * so they are rendered as-is with no time-zone shift. Otherwise a transaction
 * dated the 1st would read as the previous day for viewers west of the zone.
 */
@Pipe({
  name: 'lsDate',
  standalone: true,
})
export class LsDatePipe implements PipeTransform {
  private locale = inject(LOCALE_ID);

  transform(
    value: string | number | Date | null | undefined,
    format = 'mediumDate',
    _timezone?: string,
    locale?: string,
  ): string | null {
    const resolvedLocale = locale ?? this.locale;
    if (isDateOnlyString(value)) {
      return formatCalendarDate(value, format, resolvedLocale) || null;
    }
    const instant = toAppInstant(value);
    if (instant === null) {
      return null;
    }
    return formatDate(instant, format, resolvedLocale, appTimeZoneOffset(instant));
  }
}

/**
 * Rebinds the built-in DatePipe token to {@link LsDatePipe} so that existing
 * `inject(DatePipe)` / `datePipe.transform(...)` call sites automatically render
 * in the app time zone without any code change. Provided once at bootstrap.
 */
export const LS_DATE_PIPE_PROVIDER: Provider = {
  provide: DatePipe,
  useClass: LsDatePipe,
};
